/**
 * @file TabConversation.jsx
 * @description TabConversation component for displaying and managing tab conversation.
 * @component TabConversation
 * @exports TabConversation - Main component for displaying and managing tab conversation.
 */

import { Button, Typography, Box } from "@mui/material";
import React, { useState, useEffect } from "react";
import CardComponent from "../../../common/components/CardComponent";
import {
  TEXT_ERRORS,
  TEXT_MESSAGE,
  TEXT_STRING,
} from "../../../common/constants/agentModule/textConstants";
import { VARIANTS } from "../../../common/constants/commonConstants";
import {
  approveAndSendChatConversation,
  getTextConversationById,
  updateCaraResponseChat,
} from "../../../services/agentModule/text/text-conversation";
import { useTheme } from "@mui/material/styles";
import EditCaraResponseModal from "../../agentModule/common/EditCaraResponseModal";
import ProgressOverlay from "../../../common/components/ProgressOverlay";

const TabConversation = ({ customerguid }) => {
  const [customer, setCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProgress, setIsLoadingProgress] = useState(false);
  const [openEditCaraModal, setopenEditCaraModal] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    const fetchTextConversations = async () => {
      try {
        setIsLoading(true);
        const fetchedChatRecords = await getTextConversationById(customerguid);
        if (fetchedChatRecords && fetchedChatRecords.length > 0) {
          setCustomer(fetchedChatRecords[0]);
        }
      } catch (e) {
        console.error(TEXT_ERRORS.FETCH_TEXT_CONVERSATION_BY_ID_ERROR, e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTextConversations();
  }, [customerguid]);

  const handleEditClick = (chat) => {
    setSelectedChat({ ...chat, id: chat.id });
    setopenEditCaraModal(true);
  };

  const handleEditCaraResponseSave = async () => {
    if (selectedChat) {
      const updatedData = {
        id: selectedChat.id,
        cararesponse: selectedChat.cararesponse,
        cararesponseapproved: selectedChat.cararesponseapproved,
        userguid: localStorage.getItem("userguid")
      };
      try {
        setopenEditCaraModal(false);
        setIsLoading(true);
        await updateCaraResponseChat(updatedData);
        const updatedChatData = customer.chatData.map((item) =>
          item.id === updatedData.id
            ? { ...item, cararesponse: updatedData.cararesponse }
            : item
        );
        setCustomer({ ...customer, chatData: updatedChatData });
      } catch (error) {
        console.error(TEXT_ERRORS.UPDATE_CARA_RESPONSE_ERROR, error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleModalClose = () => {
    setopenEditCaraModal(false);
    setSelectedChat(null);
  };

  const handleApproveAndSend = async (chat) => {
    setIsLoadingProgress(true);
    const data = {
        id: chat.id,
        cararesponseapproved: true,
        userguid: localStorage.getItem("userguid")
      };
    try {
      await approveAndSendChatConversation(data)
       const updatedChatData = customer.chatData.map((item) =>
          item.id === data.id
            ? { ...item, cararesponseapproved: data.cararesponseapproved.cararesponse }
            : item
        );
        setCustomer({ ...customer, chatData: updatedChatData });
    } catch (error) {
        console.error(TEXT_ERRORS.APPROVE_SEND_CHAT_ERROR, error);
    } finally {
      setIsLoadingProgress(false);
    }
  };

  return (
    <Box
      sx={{
        pr: 2,
        overflowY: "auto",
      }}
    >
      {isLoadingProgress && (<ProgressOverlay />)}
      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : customer ? (
        customer.chatData?.length ? (
          <CardComponent hasContentBg={true}>
            {customer.chatData.map((chat, index) => (
              <Box key={index} className="ai-chatbox">
                
                <Box
                  className="ai-chatbox__message ai-chatbox__message--response"
                  sx={{
                    borderColor:
                      chat.cararesponseapproved === 0
                        ? theme.palette.warning.main
                        : theme.palette.primary.main + "30",
                  }}
                >
                  <Box className="ai-chatbox__message-content">
                    {chat.cararesponse}
                  </Box>
                  <Box
                    className="ai-chatbox__message-footer"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Box>
                      <span>
                        <b>Cara</b> on{" "}
                        {new Date(chat.cararesponsetime).toLocaleString()}
                      </span>
                      {chat.cararesponseapproved === 0 && (
                      <Button
                        variant={VARIANTS.TEXT}
                        onClick={() => handleApproveAndSend(chat)}
                      >
                        {TEXT_STRING.APPROVE_AND_SEND}
                      </Button>
                      )}
                    </Box>
                    <Box>
                      {chat.cararesponseapproved === 0 && (
                        <>
                          <Button
                            variant={VARIANTS.TEXT}
                            onClick={() => handleEditClick(chat)}
                          >
                            {TEXT_STRING.EDIT_RESPONSE}
                          </Button>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box
                  className="ai-chatbox__message ai-chatbox__message--input"
                  sx={{ borderLeftColor: theme.palette.primary.main + "60" }}
                >
                  <Box className="ai-chatbox__message-content">
                    {chat.userresponse}
                  </Box>
                  <Box className="ai-chatbox__message-footer">
                    <span>
                      <b>User</b> on{" "}
                      {new Date(chat.userresponsetime).toLocaleString()}
                    </span>
                  </Box>
                </Box>

              </Box>
            ))}
          </CardComponent>
        ) : (
          <Typography>{TEXT_MESSAGE.NO_TEXT_CONVERSATION}</Typography>
        )
      ) : (
        <Typography>No conversation found.</Typography>
      )}

      {openEditCaraModal && (
        <EditCaraResponseModal
          open={openEditCaraModal}
          selectedChat={selectedChat}
          onClose={handleModalClose}
          onSave={handleEditCaraResponseSave}
          onTextChange={(text) =>
            setSelectedChat({ ...selectedChat, cararesponse: text })
          }
          onCheckBoxChange={(checked) =>
            setSelectedChat({ ...selectedChat, cararesponseapproved: checked })
          }
        />
      )}
    </Box>
  );
};

export default TabConversation;
